import * as Sentry from '@sentry/react';
import axios from 'axios';

import {
  getToken,
  setToken,
  clearToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
} from './tokenManager';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
});

if (!process.env.REACT_APP_API_URL) {
  // eslint-disable-next-line no-console
  console.warn('[ENV] REACT_APP_API_URL não está definido. Configure seu .env');
}

let isRefreshing = false;
let failedQueue = [];

const refreshPath = '/sessions/refresh';

function processQueue(error, newToken) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(newToken);
    }
  });
  failedQueue = [];
}

async function refreshAccessToken() {
  const baseURL = process.env.REACT_APP_API_URL;
  const client = axios.create({ baseURL, timeout: 20000 });
  const rt = getRefreshToken();
  if (!rt) throw new Error('No refresh token available');
  const response = await client.post(
    refreshPath,
    { refresh_token: rt },
    { headers: { Authorization: `Refresh ${rt}` } },
  );
  const newToken = response?.data?.token;
  const newRefresh = response?.data?.refresh_token;
  if (!newToken) throw new Error('No token in refresh response');
  setToken(newToken);
  if (newRefresh) setRefreshToken(newRefresh);
  return newToken;
}

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    const message = error?.response?.data?.message;
    const originalRequest = error?.config || {};
    const status = error?.response?.status;

    if (message === 'Invalid JWT token' || status === 401) {
      if (originalRequest._retry) {
        clearToken();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: token => {
              originalRequest._retry = true;
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        refreshAccessToken()
          .then(newToken => {
            processQueue(null, newToken);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          })
          .catch(err => {
            processQueue(err, null);
            clearToken();
            clearRefreshToken();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // Capture non-auth API errors to Sentry with useful context
    Sentry.captureException(error, {
      tags: { layer: 'api' },
      extra: {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status,
        message,
      },
    });
    return Promise.reject(error);
  },
);

export default api;
export { refreshAccessToken };
